import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import {
  COUNTDOWN_STEP_MS,
  createRoundReducer,
  initialRound,
  RIGHT_FEEDBACK_MS,
  secondsLeft,
  WRONG_FEEDBACK_MS,
  type Key,
  type RoundAction,
  type RoundContext,
  type RoundState,
} from "../game/round";

const TICK_MS = 250;

export interface RoundHandle {
  state: RoundState;
  /** Seconds left, or null for an untimed round. */
  seconds: number | null;
  press: (key: Key) => void;
  /** Throw the round away. */
  stop: () => void;
  /** End the round and keep it. */
  finish: () => void;
  /** The child tapped Pass: counts as tried and wrong, shows the answer. */
  pass: () => void;
}

export interface MachineHandle<S, A> extends Omit<RoundHandle, "state"> {
  state: S;
  /** The clock, updated every tick while the round runs. */
  now: number;
  dispatch: (action: A | RoundAction) => void;
}

/**
 * Drives any reducer that contains a round with real clocks: the countdown
 * interval, the feedback timeouts and the timer tick. `roundOf` picks the
 * round out of the state. Everything else is pure, in game/round.ts and
 * whatever wraps it. The reducer and initial state are read once, on mount.
 */
export function useRoundMachine<S, A>(
  reducer: (state: S, action: A | RoundAction) => S,
  initial: () => S,
  roundOf: (state: S) => RoundState,
  seconds: number | null,
): MachineHandle<S, A> {
  const [state, dispatch] = useReducer(reducer, undefined, initial);
  const [now, setNow] = useState(() => Date.now());
  const round = roundOf(state);

  // Countdown: 3, 2, 1, then go live. With no countdown, go live at once.
  useEffect(() => {
    if (round.phase !== "countdown") return;
    const t = setTimeout(
      () => {
        if (round.count > 1) dispatch({ type: "countdown-tick" });
        else dispatch({ type: "begin", now: Date.now() });
      },
      round.count === 0 ? 0 : COUNTDOWN_STEP_MS,
    );
    return () => clearTimeout(t);
  }, [round]);

  // Timer tick while live or in feedback (timed rounds only).
  const running = (round.phase === "live" || round.phase === "feedback") && round.endAt !== null;
  useEffect(() => {
    if (!running) return;
    const tick = () => {
      const t = Date.now();
      setNow(t);
      dispatch({ type: "tick", now: t });
    };
    tick();
    const id = setInterval(tick, TICK_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [running]);

  // Feedback flash, then the next question.
  const feedbackKind = round.phase === "feedback" ? round.feedback?.kind : undefined;
  const feedbackSeq = round.phase === "feedback" ? round.tried : 0;
  useEffect(() => {
    if (!feedbackKind) return;
    const t = setTimeout(
      () => dispatch({ type: "feedback-done", now: Date.now() }),
      feedbackKind === "right" ? RIGHT_FEEDBACK_MS : WRONG_FEEDBACK_MS,
    );
    return () => clearTimeout(t);
  }, [feedbackKind, feedbackSeq]);

  const press = useCallback((key: Key) => dispatch({ type: "press", key, now: Date.now() }), []);
  const stop = useCallback(() => dispatch({ type: "stop" }), []);
  const finish = useCallback(() => dispatch({ type: "finish" }), []);
  const pass = useCallback(() => dispatch({ type: "pass", now: Date.now() }), []);

  const left = useMemo(
    () => (running ? secondsLeft(round, now) : seconds),
    [running, round, now, seconds],
  );
  return { state, now, seconds: left, press, stop, finish, pass, dispatch };
}

/**
 * A plain round (trials, practice, placement) with real clocks. `ctx` is read
 * once, when the round mounts.
 */
export function useRound(ctx: RoundContext, countdown: boolean): RoundHandle {
  const [reducer] = useState(() => createRoundReducer(ctx));
  const { state, seconds, press, stop, finish, pass } = useRoundMachine<RoundState, RoundAction>(
    reducer,
    () => initialRound(countdown ? undefined : 0),
    (s) => s,
    ctx.seconds,
  );
  return { state, seconds, press, stop, finish, pass };
}
